'use client'

import { createContext, useCallback, useContext, useMemo } from 'react'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { DeliveryTime } from '../../types/DeliveryTimes'

export interface DeliveryDaysContextType {
	delayedTime?: DeliveryTime
	hasDelayRange?: boolean
	readonly hasDelayedWareHouse: (warehousesIds?: string[]) => boolean
	readonly deliveryTimeMessage: string
}

export interface DeliveryDaysProviderProps {
	readonly children: React.ReactNode
	readonly delayedTime?: DeliveryTime
	readonly hasDelayRange?: boolean
}

export const DeliveryDaysContext = createContext<
	DeliveryDaysContextType | undefined
>(undefined)

export function DelayedDaysProvider({
	delayedTime,
	hasDelayRange,
	children,
}: DeliveryDaysProviderProps): React.ReactNode {
	const {
		country: {
			warehouse: { defaultId, showEstimatedDeliveryTime },
		},
	} = useMasterData()
	const { t } = useLabels()

	const hasDelayedWareHouse = useCallback(
		(warehousesIds?: string[]): boolean => {
			let hasDelayed = false

			if (
				showEstimatedDeliveryTime &&
				warehousesIds &&
				!warehousesIds.includes(defaultId)
			) {
				hasDelayed = true
			}

			return hasDelayed
		},
		[defaultId, showEstimatedDeliveryTime]
	)

	const getDelayedMessage = useCallback((): string => {
		if (!delayedTime) {
			return ''
		}

		const key = hasDelayRange
			? 'pdp.sizeSelector.shipmentInterval.text'
			: 'pdp.sizeSelector.shipmentDays.text'

		const placeholderValues = {
			1: hasDelayRange ? delayedTime.minimumTime : delayedTime.maximumTime,
			2: delayedTime?.maximumTime,
		}

		return replacePlaceholders(t(key), placeholderValues)
	}, [delayedTime, hasDelayRange, t])

	const value = useMemo(
		() => ({
			delayedTime,
			hasDelayRange,
			hasDelayedWareHouse,
			deliveryTimeMessage: getDelayedMessage(),
		}),
		[delayedTime, hasDelayRange, getDelayedMessage, hasDelayedWareHouse]
	)

	return (
		<DeliveryDaysContext.Provider value={value}>
			{children}
		</DeliveryDaysContext.Provider>
	)
}

/** @throws {Error} If useDelayedDays is used without DelayedDaysProvider */
export const useDelayedDays = (): DeliveryDaysContextType => {
	const context = useContext(DeliveryDaysContext)
	if (!context) {
		throw new Error('useDelayedDays must be used within a DelayedDaysProvider')
	}
	return context
}
